import apiService from 'services/apiService';
import { NavigateFunction } from 'react-router-dom';
import { AuthActionTypes, AuthActions } from 'store/auth/types';
import { Dispatch } from 'react';
import { RootState } from 'store/reducers';
import { IUser } from 'models/IUser';
import { IAuth } from 'models/IAuth';
import { GlobalActionTypes, GlobalActions } from 'store/global/types';

type FormData = Partial<IUser> & { token: string };
type Actions = AuthActions | GlobalActions;

export const loadMe = () => async (dispatch: Dispatch<Actions>, getState: () => RootState) => {
  dispatch({ type: GlobalActionTypes.LOADING });
  try {
    const options = attachTokenToHeaders(getState);
    const response = await apiService.get('/api/users/me', options);

    dispatch({
      type: GlobalActionTypes.SUCCESS_LOAD,
      payload: { me: response.data.me },
    });
    return dispatch({
      type: AuthActionTypes.LOAD_PROFILE_SUCCESS,
      payload: { me: response.data.me },
    });
  } catch (err: any) {
    // 401 means not authenticated — silent fail, just mark app as loaded
    if (err.response?.status === 401) {
      return dispatch({
        type: GlobalActionTypes.MESSAGE_FAIL,
        payload: { error: null, appLoaded: true },
      });
    }
    const errorMessage = err.response?.data?.message || 'Failed to load user';
    return dispatch({
      type: GlobalActionTypes.MESSAGE_FAIL,
      payload: { error: errorMessage, appLoaded: true },
    });
  }
};

export const loginUserWithEmail =
  (formData: FormData, navigate: NavigateFunction) =>
  async (dispatch: Dispatch<Actions | typeof loadMe>) => {
    dispatch({ type: GlobalActionTypes.LOADING });
    try {
      // Save reCAPTCHA token if provided
      if (formData.token) {
        localStorage.setItem('recaptcha_token', formData.token);
      }

      const response = await apiService.post('/auth/login', formData);

      dispatch({
        type: AuthActionTypes.LOGIN_WITH_EMAIL_SUCCESS,
        payload: { token: response.data.token, me: response.data.me },
      });

      await dispatch(loadMe);
      navigate('/', { state: { message: 'Successfully logged in!' } });
    } catch (err: any) {
      dispatch({
        type: AuthActionTypes.LOGIN_WITH_EMAIL_FAIL,
      });

      dispatch({
        type: GlobalActionTypes.MESSAGE_FAIL,
        payload: { error: err?.response?.data?.message || err.message },
      });
    }
  };

export const logInUserWithOauth = (token: string, navigate?: NavigateFunction) => async (dispatch: Dispatch<Actions>) => {
  dispatch({ type: GlobalActionTypes.LOADING });

  try {
    const headers = {
      'Content-Type': 'application/json',
      'x-auth-token': token,
    };

    const response = await apiService.get('/api/users/me', { headers });

    dispatch({
      type: AuthActionTypes.LOGIN_WITH_OAUTH_SUCCESS,
      payload: { me: response.data.me, token },
    });
    dispatch({
      type: GlobalActionTypes.SUCCESS_LOAD,
      payload: { me: response.data.me },
    });
    
    if (navigate) {
      navigate('/', { state: { message: 'Successfully logged in!' } });
    }
  } catch (err: any) {
    dispatch({
      type: GlobalActionTypes.MESSAGE_FAIL,
      payload: { error: err?.response?.data?.message || err.message },
    });
  }
};

export const logOutUser = () => async (dispatch: Dispatch<AuthActions | GlobalActions>) => {
  try {
    deleteAllCookies();
    await apiService.get('/auth/logout');

    dispatch({
      type: AuthActionTypes.LOGOUT_SUCCESS,
    });
    dispatch({
      type: GlobalActionTypes.SUCCESS_LOAD,
    });
  } catch (err) {}
};

export const forgotPassword = (formData: FormData) => async (dispatch: Dispatch<Actions>) => {
  dispatch({ type: GlobalActionTypes.LOADING });
  try {
    const response = await apiService.post('/auth/forgot-password', formData);
    return dispatch({
      type: GlobalActionTypes.SUCCESS_LOAD,
      payload: { message: response.data.message },
    });
  } catch (err: any) {
    return dispatch({
      type: GlobalActionTypes.MESSAGE_FAIL,
      payload: { error: err?.response?.data?.message || err.message },
    });
  }
};

export const changePassword = (formData: any) => async (dispatch: Dispatch<Actions>) => {
  dispatch({ type: GlobalActionTypes.LOADING });
  try {
    const response = await apiService.post('/auth/change-password', formData);

    return dispatch({
      type: GlobalActionTypes.SUCCESS_LOAD,
      payload: { message: response.data.message },
    });
  } catch (err: any) {
    return dispatch({
      type: GlobalActionTypes.MESSAGE_FAIL,
      payload: { error: err?.response?.data?.message || err.message },
    });
  }
};

export const verifyAccount =
  (formData: any, navigate: NavigateFunction) =>
  async (dispatch: Dispatch<Actions>, getState: () => RootState) => {
    dispatch({ type: GlobalActionTypes.LOADING });
    try {
      const options = attachTokenToHeaders(getState);
      const response = await apiService.post('/auth/verify-account', formData, options);
      dispatch({
        type: GlobalActionTypes.SUCCESS_LOAD,
        payload: { message: response.data.message },
      });
      navigate('/', { state: { message: 'Email Verified Successfully!' } });
    } catch (err: any) {
      return dispatch({
        type: GlobalActionTypes.MESSAGE_FAIL,
        payload: { error: err?.response?.data?.message || err.message },
      });
    }
  };

export const sendVerificationCode =
  (formData: any, navigate: NavigateFunction) =>
  async (dispatch: Dispatch<Actions>, getState: () => RootState) => {
    dispatch({ type: GlobalActionTypes.LOADING });

    try {
      const options = attachTokenToHeaders(getState);
      const response = await apiService.post('/auth/send-verification-code', formData, options);
      dispatch({
        type: GlobalActionTypes.SUCCESS_LOAD,
        payload: { message: response.data.message },
      });

      navigate('/users/identity_verification', {
        state: { message: 'Verification code sent again.' },
      });
    } catch (err: any) {
      return dispatch({
        type: GlobalActionTypes.MESSAGE_FAIL,
        payload: { error: err?.response?.data?.message || err.message },
      });
    }
  };

function deleteAllCookies() {
  var cookies = document.cookie.split(';');

  for (var i = 0; i < cookies.length; i++) {
    var cookie = cookies[i];
    var eqPos = cookie.indexOf('=');
    var name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
    document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT';
  }
}

export const attachTokenToHeaders = (
  getState: () => {
    auth: IAuth;
  },
  isFormData = false
) => {
  const token = getState().auth.token;

  const config: any = {
    headers: {},
  };

  if (!isFormData) {
    config.headers['Content-type'] = 'application/json';
  }

  if (token) {
    config.headers['x-auth-token'] = token;
  }

  return config;
};

export const clearErrors = () => (dispatch: Dispatch<Actions>) => {
  dispatch({ type: GlobalActionTypes.CLEAR_ERRORS });
};
